import {
  CONTACT_EMAIL,
  LOGO_PATH,
  OG_IMAGE_PATH,
  SITE_ALTERNATE_NAMES,
  SITE_DESCRIPTION,
  SITE_NAME,
  SITE_URL,
} from "@/constants/site";
import { localizePath } from "@/navigations/locale";

export const OG_IMAGE_URL = `${SITE_URL}${OG_IMAGE_PATH}`;
export const LOGO_URL = `${SITE_URL}${LOGO_PATH}`;
export const DEFAULT_ROBOTS = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

export type JsonLd = Record<string, unknown>;

export function absoluteUrl(path = "/", lang?: string): string {
  const localized = lang ? localizePath(path, lang) : path;
  const normalized = localized.startsWith("/") ? localized : `/${localized}`;
  if (normalized === "/") return `${SITE_URL}/`;
  return `${SITE_URL}${normalized}`;
}

export function organizationJsonLd(): JsonLd {
  return {
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: SITE_NAME,
    alternateName: [...SITE_ALTERNATE_NAMES],
    url: SITE_URL,
    email: CONTACT_EMAIL,
    logo: { "@type": "ImageObject", url: LOGO_URL },
    image: OG_IMAGE_URL,
    description: SITE_DESCRIPTION,
    areaServed: { "@type": "Country", name: "Canada" },
    address: {
      "@type": "PostalAddress",
      addressLocality: "Vancouver",
      addressRegion: "BC",
      addressCountry: "CA",
    },
    knowsAbout: [
      "AquaWorks",
      "AquaWork",
      "water quality",
      "sustainability education",
      "environmental data",
      "student research",
      "aquatic ecosystems",
    ],
  };
}

export function websiteJsonLd(): JsonLd {
  return {
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    name: SITE_NAME,
    alternateName: [...SITE_ALTERNATE_NAMES],
    url: SITE_URL,
    description: SITE_DESCRIPTION,
    inLanguage: ["en-CA", "fr-CA", "zh"],
    publisher: { "@id": `${SITE_URL}/#organization` },
  };
}

export function researchProjectJsonLd(description: string, url: string): JsonLd {
  return {
    "@type": "ResearchProject",
    "@id": `${url.replace(/#.*$/, "")}#project`,
    name: SITE_NAME,
    alternateName: [...SITE_ALTERNATE_NAMES],
    description,
    url,
    image: OG_IMAGE_URL,
    founder: { "@id": `${SITE_URL}/#organization` },
    keywords: "AquaWorks, AquaWork, Vancouver campus, water quality, monitoring robot",
  };
}

export function breadcrumbJsonLd(items: { name: string; path: string }[], lang?: string): JsonLd {
  return {
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path, lang),
    })),
  };
}

export function collectionJsonLd(name: string, description: string, url: string): JsonLd {
  return {
    "@type": "CollectionPage",
    name,
    description,
    url,
  };
}

export function articleJsonLd(
  headline: string,
  description: string,
  url: string,
  datePublished?: string,
): JsonLd {
  return {
    "@type": "Article",
    headline,
    description,
    url,
    datePublished,
    image: OG_IMAGE_URL,
    author: { "@id": `${SITE_URL}/#organization` },
    publisher: { "@id": `${SITE_URL}/#organization` },
    mainEntityOfPage: url,
  };
}

export function contactJsonLd(name: string, description: string, url: string): JsonLd {
  return {
    "@type": "ContactPage",
    url,
    name,
    description,
    mainEntity: { "@id": `${SITE_URL}/#organization` },
  };
}

export function graphJsonLd(nodes: JsonLd[]): JsonLd {
  return {
    "@context": "https://schema.org",
    "@graph": [organizationJsonLd(), websiteJsonLd(), ...nodes],
  };
}
